import { clsx } from 'clsx';
import { AlertTriangle, Users } from 'lucide-react';
import { Card } from '../common/Card';
import { StatusBadge } from '../common/StatusBadge';
import type { Consensus, PublicFramework } from '../../types';

interface ConsensusPanelProps {
  consensus: Consensus;
  framework?: PublicFramework;
}

export function ConsensusPanel({ consensus, framework }: ConsensusPanelProps) {
  const highVariance = consensus.high_variance_criteria ?? [];
  const criterionLabels = framework
    ? Object.fromEntries(
        framework.sections.flatMap((s) => s.criteria.map((c) => [c.id, c.name]))
      )
    : {};

  return (
    <Card className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-wolf-700/50">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-action" />
          <span className="text-action font-mono text-sm">CONSENSUS</span>
          <span className="text-wolf-300 font-medium">Final Result</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-baseline gap-1">
            <span
              className={clsx(
                'font-mono text-2xl font-bold',
                getScoreColor(consensus.final_score)
              )}
            >
              {consensus.final_score.toFixed(1)}
            </span>
            <span className="text-wolf-500 text-sm">/100</span>
          </div>
          <StatusBadge status={consensus.final_decision} />
        </div>
      </div>

      <div className="space-y-6">
        {/* Reasoning */}
        {consensus.reasoning && (
          <div>
            <h4 className="text-wolf-300 font-medium mb-2">Reasoning</h4>
            <p className="text-wolf-400 text-sm whitespace-pre-wrap">
              {consensus.reasoning}
            </p>
          </div>
        )}

        {/* High-variance criteria (most reviewer disagreement) */}
        {highVariance.length > 0 && (
          <div className="border border-amber-400/30 bg-amber-400/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <h4 className="text-amber-400 font-medium">High Variance</h4>
            </div>
            <p className="text-wolf-500 text-xs mb-3">
              Criteria with the most disagreement between evaluators
            </p>
            <ul className="space-y-1">
              {highVariance.map((id) => (
                <li key={id} className="text-wolf-100 text-sm">
                  {criterionLabels[id] ?? id}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Aggregated scores by section */}
        {Object.keys(consensus.aggregated_scores).length > 0 && (
          <div>
            <h4 className="text-wolf-300 font-medium mb-4">Aggregated Scores</h4>
            {framework ? (
              <div className="space-y-4">
                {framework.sections.map((section) => {
                  const rows = section.criteria.filter(
                    (c) => consensus.aggregated_scores[c.id]
                  );
                  if (rows.length === 0) return null;
                  return (
                    <div key={section.id} className="border border-wolf-700/50 p-4">
                      <h5 className="text-wolf-100 font-medium mb-3">
                        {section.id}. {section.name}
                      </h5>
                      <div className="space-y-2">
                        {rows.map((criterion) => (
                          <AggregatedScoreRow
                            key={criterion.id}
                            name={criterion.name}
                            entry={consensus.aggregated_scores[criterion.id]}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(consensus.aggregated_scores).map(([id, entry]) => (
                  <AggregatedScoreRow key={id} name={id} entry={entry} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Evaluator count */}
        <p className="text-wolf-500 text-xs">
          Based on {consensus.evaluator_count} evaluation
          {consensus.evaluator_count === 1 ? '' : 's'}
        </p>
      </div>
    </Card>
  );
}

function AggregatedScoreRow({
  name,
  entry,
}: {
  name: string;
  entry: { score: number; source: string };
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex-shrink-0 w-14">
        <span
          className={clsx(
            'font-mono text-lg font-bold',
            getIndividualScoreColor(entry.score)
          )}
        >
          {entry.score.toFixed(1)}
        </span>
        <span className="text-wolf-500 text-xs">/5</span>
      </div>
      <span className="text-wolf-100 text-sm flex-1">{name}</span>
      <span className="text-wolf-500 text-xs font-mono">{entry.source}</span>
    </div>
  );
}

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-action';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function getIndividualScoreColor(score: number): string {
  if (score >= 4) return 'text-action';
  if (score >= 3) return 'text-amber-400';
  if (score >= 2) return 'text-orange-400';
  return 'text-red-400';
}
